import "@nectary/components/spinner";
import { useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import React from "react";
import { authStrategy } from "../../auth";
import { CookieBanner } from "../../components/CookieBanner/CookieBanner";
import {
  isCookieConsentUnset,
  useIsConsentRequired,
} from "../../components/CookieBanner/consent";
import { updateGtagConsent } from "../../components/CookieBanner/gtag-consent";
import {
  getOnesaasConsent,
  onesaasConsentAtom,
  syncSharedStorageConsent,
  updateOnesaasConsentCookie,
} from "../../components/CookieBanner/onesaas-consent-cookie";
import { storeUtmParametersInCookie } from "../../handleUtmParameters";
import { IntegrationsProviderWithData } from "../../hooks/integrationsProvider";
import { SidebarContextProvider } from "../../hooks/state";
import { ShellIntlProvider, UserlessIntlProvider } from "../../intl";
import { globalJotaiStore } from "../../jotai-store";
import { traceManager } from "../../sentry";
import { UserProvider } from "../../user-context";
import { AuthenticatedRoutes, PublicRoutes } from "./RouteGenerators";
import { ShellWrapper } from "./ShellWrapper";
import { LoadingScreenContainer } from "./components/LoadingScreen";
import { useMmToken } from "./hooks/useMMToken";

export const App = () => {
  const token = useMmToken();
  const [startTime] = useState(Date.now());
  const [appReady, setAppReady] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const setConsent = useSetAtom(onesaasConsentAtom);
  const shouldHandleConsent = useIsConsentRequired();

  useEffect(() => {
    if (shouldHandleConsent) {
      // Sync consent from cross-domain storage first (runs in background)
      syncSharedStorageConsent().finally(() => {
        const consent = getOnesaasConsent();
        setConsent(getOnesaasConsent());
        if (isCookieConsentUnset(consent)) {
          setShowCookieBanner(true);
        } else {
          updateGtagConsent(
            consent.onesaascookie_acceptance,
            consent.onesaascookie_performance,
            consent.onesaascookie_targeting
          );
        }
      });
    }
  }, [setConsent, shouldHandleConsent]);

  useEffect(() => {
    storeUtmParametersInCookie(globalJotaiStore);
  }, []);

  useEffect(() => {
    if (token) {
      const undoPatchRequests = authStrategy.patchRequests();
      setAppReady(true);
      return undoPatchRequests;
    } else {
      setAppReady(false);
    }
    return;
  }, [token]);

  const transaction = traceManager.getTransaction("TimeToFirstMFELoad");
  transaction?.setAttribute("is_public_route", String(!token));

  return appReady && token ? (
    <UserProvider
      Fallback={() => (
        <LoadingScreenContainer>
          <sinch-spinner size="l" />
        </LoadingScreenContainer>
      )}
    >
      <ShellIntlProvider>
        <SidebarContextProvider>
          <IntegrationsProviderWithData>
            <ShellWrapper>
              <AuthenticatedRoutes startTime={startTime} />
              {showCookieBanner && (
                <CookieBanner
                  updateConsent={updateOnesaasConsentCookie}
                  closeBanner={() => setShowCookieBanner(false)}
                />
              )}
            </ShellWrapper>
          </IntegrationsProviderWithData>
        </SidebarContextProvider>
      </ShellIntlProvider>
    </UserProvider>
  ) : (
    <UserlessIntlProvider>
      <PublicRoutes />
      {showCookieBanner && (
        <CookieBanner
          updateConsent={updateOnesaasConsentCookie}
          closeBanner={() => setShowCookieBanner(false)}
        />
      )}
    </UserlessIntlProvider>
  );
};
